export enum StorageType {
  SESSION_STORAGE = 'sessionStorage',
  LOCAL_STORAGE = 'localStorage',
}

export enum Keyspace {
  GUID = 'guid',
  REQUEST_UUID = 'request_uuid',
}

export interface Guid {
  id?: string | null;
  dateCreated?: string | null;
}

export type ErrorCallback = (err: unknown) => void;

export interface ClientStorage {
  get<T>(errorCallback: ErrorCallback): T | null;
  set<T>(data: T, errorCallback: ErrorCallback): void;
  isEmpty(errorCallback: ErrorCallback): boolean;
  isExpired(errorCallback: ErrorCallback): boolean;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const NARVARJS_NAMESPACE = 'narvarjs';
const NAMESPACE_SEPERATOR = ':';

const localStorage = (() => {
  if (typeof window === 'undefined') return undefined;
  try {
    // reading the localStorage property can throw if we're in an iframe
    // see: https://stackoverflow.com/questions/67645164
    return window.localStorage;
  } catch (err) {
    console.error('Error reading localStorage', err);
    return undefined;
  }
})();
let inMemoryGuid: unknown;

export default (
  storageType: StorageType = StorageType.LOCAL_STORAGE,
  keySpace: Keyspace = Keyspace.GUID,
): ClientStorage => {
  const storage = Object.freeze({
    get: <T>(errorCallback: ErrorCallback): T | null => {
      if (!localStorage && inMemoryGuid) return inMemoryGuid as T;
      try {
        const unparsedData = window[storageType].getItem(
          [NARVARJS_NAMESPACE, keySpace].join(NAMESPACE_SEPERATOR),
        );
        if (!unparsedData) return null;
        inMemoryGuid = JSON.parse(unparsedData);
        return inMemoryGuid as T;
      } catch (err) {
        errorCallback(err);
        return null;
      }
    },
    set: (data: unknown, errorCallback: ErrorCallback): void => {
      inMemoryGuid = data;
      try {
        window[storageType].setItem(
          [NARVARJS_NAMESPACE, keySpace].join(NAMESPACE_SEPERATOR),
          JSON.stringify(data),
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    isExpired: (errorCallback: ErrorCallback): boolean => {
      const { dateCreated = null } = storage.get<Guid>(errorCallback) || {};
      return dateCreated
        ? Date.now() - new Date(dateCreated.toString()).getTime() > ONE_DAY_MS
        : true;
    },
    isEmpty: (errorCallback: ErrorCallback): boolean => {
      return !storage.get(errorCallback);
    },
  });

  return storage;
};
